import { useState, useEffect, useCallback, useRef } from "react";
import { X, FileText, CheckCircle } from "lucide-react";
import { LottieSuccess } from "@/components/LottieAnimations";
import analytics from "@/utils/analytics";
import LeadForm from "@/forms/engine/LeadForm";
import leadPopup from "@/forms/definitions/leadPopup";
import {
  canShowLeadPopup,
  markPopupShown,
  markPopupDismissed,
} from "@/utils/journey";

/**
 * ─────────────────────────────────────────────────────────────────────────
 * LEAD POPUP — SECONDARY / FALLBACK CONVERSION PATH
 * ─────────────────────────────────────────────────────────────────────────
 * This popup is a FALLBACK conversion channel, not a primary CTA.
 *
 * Rules for anyone modifying this file:
 *   1. Primary journeys ALWAYS win. If a user has engaged with any primary
 *      form (contact_form, booking_zoho, booking_fallback, or a calculator
 *      lead form) or has already captured a lead this session, the popup
 *      must NOT appear.
 *   2. Suppression is owned by `utils/journey.js` — never inline new ad-hoc
 *      suppression logic here. If you add a new trigger condition, first
 *      check `canShowLeadPopup()` and respect its result.
 *   3. `analytics.formStart / formSubmit / generateLead` automatically
 *      update the journey state for you. Do NOT duplicate those writes.
 *   4. Never add the popup to routes that already host a visible lead form
 *      — extend SUPPRESSED_PATH_PREFIXES in journey.js instead.
 *   5. `popup_shown` and `popup_dismissed` are intentionally not
 *      conversions. `generate_lead` remains the single primary conversion.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Trigger thresholds
const TIME_ON_PAGE_MS = 20_000; // 20 seconds
const SCROLL_PCT_THRESHOLD = 0.6; // 60%
// How often we re-check suppression while waiting for thresholds.
const POLL_INTERVAL_MS = 1000;

/**
 * Journey-aware trigger:
 *  - waits until user has been on page >= 20s AND scrolled past 60%
 *  - re-checks journey controller right before showing
 *  - never shows more than once per session
 */
function useJourneyPopupTrigger() {
  const [visible, setVisible] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    // Fast-exit if journey controller already says no.
    if (!canShowLeadPopup().allowed) return undefined;

    const startedAt = Date.now();
    const getScrollPct = () => {
      const h = document.body.scrollHeight - window.innerHeight;
      return h > 0 ? window.scrollY / h : 0;
    };
    // Seed with current scroll — a user landing already past the threshold
    // (short page, anchor link, SPA back-nav) must still be counted.
    let scrollOk = getScrollPct() >= SCROLL_PCT_THRESHOLD;

    const onScroll = () => {
      if (getScrollPct() >= SCROLL_PCT_THRESHOLD) scrollOk = true;
    };

    const tryShow = () => {
      if (firedRef.current) return;
      const timeOk = Date.now() - startedAt >= TIME_ON_PAGE_MS;
      if (!(timeOk && scrollOk)) return;
      // Re-check journey state right before showing — primary form may
      // have started while we were waiting.
      if (!canShowLeadPopup().allowed) return;
      firedRef.current = true;
      markPopupShown();
      analytics.popupShown(window.location.pathname);
      setVisible(true);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    const poll = window.setInterval(tryShow, POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.clearInterval(poll);
    };
  }, []);

  return [visible, setVisible];
}

// Trigger rule shown in the Lead's Note so sales know how the visitor arrived.
const POPUP_CONTEXT = {
  popup: "Free Payment Health Check",
  trigger: "20s on page + 60% scroll",
};

function PopupSuccess({ onDismiss }) {
  return (
    <div className="text-center py-4" data-testid="lead-popup-success">
      <LottieSuccess size={64} className="mx-auto mb-2" />
      <h3 className="font-heading text-xl font-bold text-brand-black mb-1">{leadPopup.success.title}</h3>
      <p className="text-sm text-brand-grey">{leadPopup.success.body}</p>
      <button onClick={onDismiss} className="mt-4 text-sm text-brand-aqua font-semibold hover:underline">Continue browsing</button>
    </div>
  );
}

function PopupIntro() {
  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
          <FileText size={22} className="text-brand-aqua" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold text-brand-black">Free Payment Health Check</h3>
          <p className="text-xs text-brand-grey">Takes 30 seconds. No obligation.</p>
        </div>
      </div>
      <p className="text-sm text-brand-grey leading-relaxed mb-4">
        Enter your details and we&apos;ll send you a personalised review of your payment setup — including where you might be overpaying and what a better deal could look like.
      </p>
      <div className="space-y-2.5 mb-4">
        {["Side-by-side cost comparison", "Hidden fee identification", "Personalised savings estimate"].map((item) => (
          <div key={item} className="flex items-center gap-2 text-sm text-brand-black">
            <CheckCircle size={14} className="text-brand-aqua flex-shrink-0" />{item}
          </div>
        ))}
      </div>
    </>
  );
}

export default function LeadPopup() {
  const [visible, setVisible] = useJourneyPopupTrigger();
  const [converted, setConverted] = useState(false);

  const dismiss = useCallback(() => {
    setVisible(false);
    markPopupDismissed();
    // Only emit dismissed when the user didn't convert (success closes via its own CTA).
    if (!converted) analytics.popupDismissed(window.location.pathname);
  }, [setVisible, converted]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="lead-popup-overlay">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[calc(100vh-2rem)] overflow-y-auto p-6 sm:p-8 animate-slide-up" data-testid="lead-popup">
        <button onClick={dismiss} className="absolute top-4 right-4 text-brand-grey hover:text-brand-black transition-colors" data-testid="lead-popup-close" aria-label="Close">
          <X size={20} />
        </button>
        {!converted && <PopupIntro />}
        <LeadForm
          schema={leadPopup}
          context={POPUP_CONTEXT}
          revealed={visible}
          variant="compact"
          onSuccess={() => setConverted(true)}
          renderSuccess={() => <PopupSuccess onDismiss={dismiss} />}
        />
      </div>
    </div>
  );
}
